/** Admin — Brand applications queue. Approve or reject with reason. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, ChevronLeft, FileText, Eye } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";

const STATUSES = ["pending", "approved", "rejected", "all"];

export default function AdminBrandApplications() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  const load = async () => {
    setRows(null);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await apiClient.get("/marketplace/admin/brand-applications", { params });
      setRows(data);
    } catch (e) {
      setRows([]);
      toast.error(e?.response?.data?.detail || "Could not load");
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    load();
  }, [filter, isAdmin]);

  const approve = async (id) => {
    try {
      await apiClient.post(`/marketplace/admin/brand-applications/${id}/approve`);
      toast.success("Brand approved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve");
    }
  };

  const submitReject = async () => {
    if ((reason || "").trim().length < 10) {
      return toast.error("Rejection reason must be at least 10 characters");
    }
    try {
      await apiClient.post(`/marketplace/admin/brand-applications/${rejecting}/reject`, {
        reason: reason.trim(),
      });
      toast.success("Application rejected");
      setRejecting(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reject");
    }
  };

  const viewDoc = async (appId, kind) => {
    try {
      const { data } = await apiClient.get(
        `/marketplace/admin/brand-applications/${appId}/doc/${kind}`
      );
      if (!data.doc_b64) return toast.error("Document missing");
      const w = window.open("", "_blank");
      if (!w) return toast.error("Popup blocked — allow popups to view");
      if (data.doc_b64.startsWith("data:application/pdf")) {
        w.document.write(
          `<iframe src="${data.doc_b64}" style="width:100%;height:100vh;border:0"></iframe>`
        );
      } else {
        w.document.write(
          `<img src="${data.doc_b64}" style="max-width:100%;height:auto;display:block;margin:auto"/>`
        );
      }
    } catch {
      toast.error("Could not load document");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen grid place-items-center" style={{ background: "#F4F3F1" }}>
        <div className="text-ink-soft">Sign in to continue</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen grid place-items-center px-6 text-center" style={{ background: "#F4F3F1" }}>
        <div>
          <h1 className="font-barlow-cond font-extrabold text-[24px]">Not authorised</h1>
          <p className="text-[13px] text-ink-soft mt-1">This page is admin-only.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="app-page min-h-screen pb-24"
      data-testid="admin-brand-applications"
      style={{ background: "#F4F3F1" }}
    >
      <header className="sticky top-0 z-20 bg-canvas/90 backdrop-blur-xl border-b border-line2">
        <div className="max-w-4xl mx-auto h-14 px-3 flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            data-testid="admin-brand-back"
            className="w-10 h-10 grid place-items-center"
          >
            <ChevronLeft size={22} />
          </button>
          <h1 className="font-barlow-cond font-extrabold text-[22px]">Brand applications</h1>
        </div>
        <div className="max-w-4xl mx-auto px-3 pb-3 flex gap-2">
          {STATUSES.map((s) => {
            const on = filter === s;
            return (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`brand-filter-${s}`}
                className="h-9 px-4 rounded-pill text-[12px] font-semibold"
                style={{
                  background: on ? "#00D4FF" : "#ECEAE7",
                  color: on ? "#0B0B0B" : "#6B6760",
                  borderRadius: 100,
                }}
              >
                {s[0].toUpperCase() + s.slice(1)}
              </button>
            );
          })}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 pt-4 space-y-3">
        {rows === null && <div className="text-center py-12 text-ink-soft">Loading…</div>}
        {rows?.length === 0 && (
          <div className="text-center py-16" data-testid="brand-apps-empty">
            <FileText size={32} className="text-ink-tertiary mx-auto" />
            <div className="font-barlow-cond text-[20px] font-bold mt-2">No applications</div>
          </div>
        )}
        {rows?.map((r) => (
          <div
            key={r.application_id}
            className="bg-white border border-line2 rounded-[14px] p-4"
            data-testid={`brand-app-${r.application_id}`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold">{r.brand_name}</div>
                <div className="text-[11.5px] text-ink-tertiary font-mono mt-0.5">
                  GSTIN {r.gstin}
                </div>
                <div className="text-[12px] text-ink-soft mt-1">
                  {r.business_category.replace("_", " ")} · {r.contact_email}
                </div>
                {r.fssai_number && (
                  <div className="text-[12px] text-ink-soft">FSSAI {r.fssai_number}</div>
                )}
              </div>
              <span
                className="shrink-0 text-[10px] font-bold uppercase px-2 h-6 inline-flex items-center rounded-pill"
                style={{
                  background:
                    r.status === "approved"
                      ? "rgba(0,160,103,0.14)"
                      : r.status === "rejected"
                      ? "rgba(229,72,77,0.14)"
                      : "rgba(192,132,0,0.14)",
                  color:
                    r.status === "approved"
                      ? "#00A067"
                      : r.status === "rejected"
                      ? "#E5484D"
                      : "#C08400",
                  borderRadius: 100,
                }}
              >
                {r.status}
              </span>
            </div>

            <div className="mt-3 flex gap-2 flex-wrap">
              <button
                onClick={() => viewDoc(r.application_id, "gst")}
                data-testid={`view-gst-${r.application_id}`}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-pill border border-line2 text-[12px] font-semibold"
                style={{ borderRadius: 100 }}
              >
                <Eye size={13} /> View GST
              </button>
              {r.fssai_number && (
                <button
                  onClick={() => viewDoc(r.application_id, "fssai")}
                  data-testid={`view-fssai-${r.application_id}`}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-pill border border-line2 text-[12px] font-semibold"
                  style={{ borderRadius: 100 }}
                >
                  <Eye size={13} /> View FSSAI
                </button>
              )}
            </div>

            {r.status === "pending" && (
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => approve(r.application_id)}
                  data-testid={`approve-${r.application_id}`}
                  className="flex-1 h-10 rounded-pill text-[13px] font-bold inline-flex items-center justify-center gap-1.5"
                  style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}
                >
                  <Check size={14} /> Approve
                </button>
                <button
                  onClick={() => setRejecting(r.application_id)}
                  data-testid={`reject-${r.application_id}`}
                  className="flex-1 h-10 rounded-pill text-[13px] font-semibold border border-line2 inline-flex items-center justify-center gap-1.5"
                  style={{ borderRadius: 100 }}
                >
                  <X size={14} /> Reject
                </button>
              </div>
            )}
            {r.status === "rejected" && r.rejection_reason && (
              <div className="mt-3 text-[12px] text-ink-soft bg-canvas2 rounded-[10px] p-2">
                <span className="font-semibold">Reason:</span> {r.rejection_reason}
              </div>
            )}
          </div>
        ))}
      </main>

      {rejecting && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-end"
          onClick={() => setRejecting(null)}
          data-testid="brand-reject-modal"
        >
          <div
            className="w-full bg-white rounded-t-[24px] p-5 max-w-2xl mx-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-barlow-cond font-extrabold text-[22px]">Reject application</h3>
            <p className="text-[12px] text-ink-soft mt-1">
              This reason will be sent to the applicant. Minimum 10 characters.
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 400))}
              placeholder="GST certificate is unclear, please re-upload…"
              data-testid="brand-reject-reason"
              className="mt-3 w-full p-3 rounded-[12px] border border-line bg-canvas2 outline-none"
              style={{ minHeight: 90 }}
            />
            <div className="flex gap-2.5 mt-3">
              <button
                onClick={() => setRejecting(null)}
                className="flex-1 h-12 rounded-pill border border-line2 font-semibold"
                style={{ borderRadius: 100 }}
              >
                Cancel
              </button>
              <button
                onClick={submitReject}
                data-testid="brand-reject-submit"
                className="flex-1 h-12 rounded-pill font-bold"
                style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
